// frontend/src/pages/admin/Simulations.tsx
import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Product {
  id: number;
  nom: string;
}

interface SimulationScenario {
  id: number;
  product_id: number;
  nom: string;
  prospect_prompt: string;
  qualification_prompt?: string;
  description?: string;
  ordre: number;
  created_at: string;
  product?: Product;
}

interface ScenarioFormData {
  product_id: number;
  nom: string;
  description: string;
  prospect_prompt: string;
  qualification_prompt: string;
  ordre: number;
}

export default function AdminSimulations() {
  const [products, setProducts] = useState<Product[]>([]);
  const [scenarios, setScenarios] = useState<SimulationScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState<SimulationScenario | null>(null);
  const [formData, setFormData] = useState<ScenarioFormData>({
    product_id: 0,
    nom: '',
    description: '',
    prospect_prompt: '',
    qualification_prompt: '',
    ordre: 1
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (products.length > 0) {
      fetchScenarios();
      // s'assure que le select produit a une valeur par défaut
      setFormData((prev) => ({ ...prev, product_id: prev.product_id || products[0].id }));
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/admin/products');
      setProducts(res.data);
      if (res.data.length > 0) {
        setFormData((prev) => ({ ...prev, product_id: res.data[0].id }));
      }
    } catch {
      toast.error('Erreur lors du chargement des produits');
    }
  };

  const fetchScenarios = async () => {
    setLoading(true);
    try {
      const all: SimulationScenario[] = [];
      for (const product of products) {
        const res = await api.get(`/products/${product.id}/simulations`);
        const data = (res.data as SimulationScenario[]).map((s) => ({ ...s, product }));
        all.push(...data);
      }
      setScenarios(all);
    } catch {
      toast.error('Erreur lors du chargement des scénarios');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (scenario: SimulationScenario) => {
    setEditingScenario(scenario);
    setFormData({
      product_id: scenario.product_id,
      nom: scenario.nom,
      description: scenario.description || '',
      prospect_prompt: scenario.prospect_prompt,
      qualification_prompt: scenario.qualification_prompt || '',
      ordre: scenario.ordre
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce scénario ?')) return;
    try {
      await api.delete(`/admin/simulations/${id}`);
      toast.success('Scénario supprimé');
      fetchScenarios();
    } catch {
      toast.error('Erreur lors de la suppression du scénario');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingScenario) {
        await api.put(`/admin/simulations/${editingScenario.id}`, formData);
        toast.success('Scénario modifié avec succès');
      } else {
        await api.post('/admin/simulations', formData);
        toast.success('Scénario créé avec succès');
      }
      setShowModal(false);
      setEditingScenario(null);
      setFormData({
        product_id: products[0]?.id || 0,
        nom: '',
        description: '',
        prospect_prompt: '',
        qualification_prompt: '',
        ordre: 1
      });
      fetchScenarios();
    } catch {
      toast.error("Erreur lors de l'enregistrement du scénario");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête (responsive) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scénarios de simulation</h1>
          <p className="mt-1 text-gray-600">Gérez les scénarios disponibles pour les formations</p>
        </div>
        <button
          onClick={() => {
            setEditingScenario(null);
            setFormData({
              product_id: products[0]?.id || 0,
              nom: '',
              description: '',
              prospect_prompt: '',
              qualification_prompt: '',
              ordre: 1
            });
            setShowModal(true);
          }}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau scénario
        </button>
      </div>

      {/* Liste des scénarios (Desktop - tableau) */}
      <div className="hidden md:block bg-white shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scénario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordre
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scenarios.map((scenario) => (
                <tr key={scenario.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{scenario.nom}</div>
                    {scenario.description && (
                      <div className="text-sm text-gray-500">{scenario.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {scenario.product?.nom}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{scenario.ordre}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                    <button
                      onClick={() => openEditModal(scenario)}
                      className="text-primary hover:text-primary-dark"
                      aria-label="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(scenario.id)}
                      className="text-red-600 hover:text-red-900"
                      aria-label="Supprimer"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Liste des scénarios (Mobile - cartes) */}
      <div className="md:hidden space-y-3">
        {scenarios.map((scenario) => (
          <div key={scenario.id} className="bg-white shadow rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 break-words">{scenario.nom}</h3>
                {scenario.description && (
                  <p className="text-sm text-gray-600 mt-1 break-words">{scenario.description}</p>
                )}
                <div className="mt-2 text-sm text-gray-500 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1">
                    Produit : <span className="font-medium text-gray-700">{scenario.product?.nom}</span>
                  </span>
                  <span className="inline-flex items-center gap-1">Ordre {scenario.ordre}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditModal(scenario)}
                  className="p-2 rounded-md text-primary hover:bg-blue-50"
                  aria-label="Modifier"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(scenario.id)}
                  className="p-2 rounded-md text-red-600 hover:bg-red-50"
                  aria-label="Supprimer"
                >
                  <Trash className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal (actions sticky compactes, adaptées au téléphone) */}
      {showModal && (
        <div className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/40">
          <div className="modal-container w-full max-w-2xl bg-white rounded-xl shadow-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingScenario ? 'Modifier le scénario' : 'Nouveau scénario'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom</label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Prompt prospect</label>
                  <textarea
                    rows={4}
                    required
                    value={formData.prospect_prompt}
                    onChange={(e) => setFormData({ ...formData, prospect_prompt: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Prompt qualification</label>
                  <textarea
                    rows={4}
                    value={formData.qualification_prompt}
                    onChange={(e) =>
                      setFormData({ ...formData, qualification_prompt: e.target.value })
                    }
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Produit</label>
                  <select
                    value={formData.product_id}
                    onChange={(e) =>
                      setFormData({ ...formData, product_id: parseInt(e.target.value, 10) })
                    }
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  >
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Ordre</label>
                  <input
                    type="number"
                    value={formData.ordre}
                    onChange={(e) =>
                      setFormData({ ...formData, ordre: parseInt(e.target.value || '0', 10) })
                    }
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              {/* Barre d’actions sticky (compacte, comme Users/Modules) */}
              <div className="mt-6 sm:mt-8 sticky bottom-0 bg-white pb-2 pt-3">
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingScenario(null);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                  >
                    {editingScenario ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
